import { toIpfsGatewayUrl, getIpfsGatewayBase } from "../utils/ipfs";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

export function normalizeTokenId(tokenId) {
  return tokenId == null ? "" : String(tokenId);
}

export function getFallbackMetadata(tokenId) {
  return {
    name: `Carbon Credit #${tokenId}`,
    description: "Carbon Credit NFT",
    image: "",
    attributes: [],
  };
}

function normalizeAddress(address) {
  return address ? String(address).toLowerCase() : null;
}

export function extractListingState(listing) {
  const active = Boolean(listing?.active ?? listing?.[2]);
  return {
    active,
    seller: listing?.seller ?? listing?.[0] ?? null,
    price: listing?.price ?? listing?.[1] ?? null,
  };
}

export function extractAuctionState(auction) {
  const active = Boolean(auction?.active ?? auction?.[5]);
  const seller = auction?.seller ?? auction?.[0] ?? null;
  const minPrice = auction?.minPrice ?? auction?.startingPrice ?? auction?.[1] ?? null;
  const highestBid = auction?.highestBid ?? auction?.[2] ?? null;
  const highestBidder = auction?.highestBidder ?? auction?.[3] ?? null;
  const endTime = auction?.endTime ?? auction?.[4] ?? null;

  return {
    active,
    seller,
    minPrice,
    startingPrice: minPrice,
    highestBid,
    highestBidder,
    endTime,
  };
}

async function fetchTokenMetadata(tokenURI, metadataByUri, setMetadataByUri, signal) {
  if (!tokenURI) {
    return {
      metadata: null,
      metadataStatus: "missing",
    };
  }

  if (metadataByUri && metadataByUri[tokenURI]) {
    return {
      metadata: metadataByUri[tokenURI],
      metadataStatus: "cached",
    };
  }

  const url = toIpfsGatewayUrl(tokenURI, getIpfsGatewayBase());
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Metadata request failed (${res.status})`);
  }

  const metadata = await res.json();
  if (setMetadataByUri) {
    setMetadataByUri(tokenURI, metadata);
  }

  return {
    metadata,
    metadataStatus: "loaded",
  };
}

export async function hydrateDynamicTokenRecord({
  tokenId,
  nftContract,
  marketplaceContract,
  account,
  existingAuctionStartBlock = null,
}) {
  const normalizedId = normalizeTokenId(tokenId);
  const [owner, retirementDetails, listingRaw, auctionRaw] = await Promise.all([
    nftContract.ownerOf(normalizedId).catch(() => null),
    nftContract.getRetirementDetails(normalizedId).catch(() => ({ isRetired: false, verifier: null, timestamp: null })),
    marketplaceContract.listings(normalizedId).catch(() => null),
    marketplaceContract.auctions(normalizedId).catch(() => null),
  ]);

  const listing = extractListingState(listingRaw);
  const auction = extractAuctionState(auctionRaw);

  const retired = Boolean(retirementDetails?.isRetired);
  const retiredBy = retirementDetails?.verifier ?? null;
  const retiredTimestamp = retirementDetails?.timestamp ?? null;

  const ownerNormalized = normalizeAddress(owner);
  const accountNormalized = normalizeAddress(account);

  const isAuction = auction.active;
  const isListed = listing.active || isAuction;
  const auctionHighestBidder = normalizeAddress(auction.highestBidder);

  const price = isAuction
    ? (auction.highestBid && Number(auction.highestBid) > 0 ? auction.highestBid : auction.minPrice)
    : listing.price;

  const seller = isAuction
    ? (auctionHighestBidder && auctionHighestBidder !== ZERO_ADDRESS ? auctionHighestBidder : auction.seller)
    : listing.seller;

  return {
    tokenId: normalizedId,
    owner,
    isOwner: Boolean(accountNormalized && ownerNormalized === accountNormalized),
    retired,
    retiredBy,
    retiredTimestamp,
    isListed,
    isAuction,
    price,
    seller,
    auctionData: isAuction
      ? {
          active: auction.active,
          seller: auction.seller,
          minPrice: auction.minPrice,
          startingPrice: auction.startingPrice,
          highestBid: auction.highestBid,
          highestBidder: auction.highestBidder,
          endTime: auction.endTime,
          auctionStartBlock: existingAuctionStartBlock,
        }
      : {
          active: false,
          seller: null,
          minPrice: null,
          startingPrice: null,
          highestBid: null,
          highestBidder: null,
          endTime: null,
          auctionStartBlock: existingAuctionStartBlock,
        },
    dynamicStatus: "ready",
    lastHydratedBlock: null,
  };
}

export async function hydrateFullTokenRecord({
  tokenId,
  nftContract,
  marketplaceContract,
  account,
  metadataByUri,
  setMetadataByUri,
  previousToken,
  signal,
}) {
  const normalizedId = normalizeTokenId(tokenId);
  const dynamicPatch = await hydrateDynamicTokenRecord({
    tokenId: normalizedId,
    nftContract,
    marketplaceContract,
    account,
    existingAuctionStartBlock: previousToken?.auctionData?.auctionStartBlock ?? null,
  });

  // Always prefer live on-chain tokenURI so cached stale URIs don't keep old metadata forever.
  const onChainTokenURI = await nftContract.tokenURI(normalizedId).catch(() => null);
  let tokenURI = onChainTokenURI || previousToken?.tokenURI || previousToken?.uri || null;

  if (!tokenURI) {
    return {
      ...dynamicPatch,
      tokenURI: null,
      uri: null,
      ...getFallbackMetadata(normalizedId),
      metadataStatus: "missing-uri",
      hiddenFromMarketplace: true,
      fullHydratedAt: Date.now(),
    };
  }

  try {
    const { metadata, metadataStatus } = await fetchTokenMetadata(
      tokenURI,
      metadataByUri,
      setMetadataByUri,
      signal
    );

    return {
      ...dynamicPatch,
      tokenURI,
      uri: tokenURI,
      ...(metadata || getFallbackMetadata(normalizedId)),
      metadataStatus,
      hiddenFromMarketplace: false,
      fullHydratedAt: Date.now(),
    };
  } catch {
    return {
      ...dynamicPatch,
      tokenURI,
      uri: tokenURI,
      ...getFallbackMetadata(normalizedId),
      metadataStatus: "fallback",
      hiddenFromMarketplace: false,
      fullHydratedAt: Date.now(),
    };
  }
}

export async function runWithConcurrency(items, worker, concurrency = 6) {
  const maxConcurrency = Math.max(1, Number(concurrency) || 1);
  const results = new Array(items.length);
  let cursor = 0;

  async function runWorker() {
    while (cursor < items.length) {
      const idx = cursor;
      cursor += 1;
      results[idx] = await worker(items[idx], idx);
    }
  }

  const workers = Array.from({ length: Math.min(maxConcurrency, items.length) }, () => runWorker());
  await Promise.all(workers);
  return results;
}
